import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type ScoringTaskType =
    | 'DescribeImage'
    | 'RetellLecture'
    | 'SummarizeGroupDiscussion'
    | 'RespondToSituation'
    | 'SummarizeWrittenText'
    | 'SummarizeSpokenText'
    | 'WriteEssay'
    | 'WriteEmail';

export type AiOperationUsage = {
    provider: string;
    model: string;
    operation: string;
    totalInvocations: number;
    completedInvocations: number;
    failedInvocations: number;
    totalTokens: number;
    p95DurationMs: number;
};

export type AiOperationObservability = {
    models: AiOperationUsage[];
    pendingQueueDepth: number;
};

export type ModelAttempt = {
    model: string;
    role: string;
    durationMs: number;
    status: string;
    failureCategory: string | null;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
};

export type ScoringObservationItem = {
    scoringObservationId: string;
    createdAtUtc: string;
    completedAtUtc: string;
    durationMs: number;
    examName: string;
    examTaskType: ScoringTaskType;
    examTaskId: string | null;
    questionNo: string | null;
    scoringMode: string;
    requestId: string | null;
    userId: string | null;
    mockTestAttemptId: string | null;
    status: string;
    issueCode: string | null;
    issueMessage: string | null;
    score: number | null;
    maxScore: number | null;
    audioDurationSeconds: number | null;
    responseWordCount: number | null;
    modelUsed: string | null;
    fallbackUsed: boolean;
    modelAttemptCount: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    traceId: string | null;
    modelAttempts: ModelAttempt[];
};

export type ScoringScoreRange = {
    examName: 'Academic' | 'Core';
    traitName: string;
    maximumScore: number;
    buckets: Array<{
        score: number;
        practiceCount: number;
    }>;
};

export type ScoringPracticeDetail = {
    scoringObservationId: string;
    practiceExamTaskAttemptId: string;
    practicedAtUtc: string;
    userId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    examName: 'Academic' | 'Core';
    examTaskType: ScoringTaskType;
    examTaskId: string;
    questionNo: string | null;
    learnerResponse: string | null;
    promptBody: string | null;
    systemResponse: Record<string, unknown> | null;
};

export type ScoringObservabilityData = {
    generatedAtUtc: string;
    fromUtc: string;
    toUtc: string;
    page: number;
    pageSize: number;
    totalRows: number;
    summary: {
        totalCount: number;
        completedCount: number;
        failedCount: number;
        cancelledCount: number;
        fallbackCount: number;
        averageDurationMs: number;
        p50DurationMs: number;
        p95DurationMs: number;
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
    };
    taskTypes: Array<{
        examTaskType: ScoringTaskType;
        displayName: string;
        totalCount: number;
        completedCount: number;
        failedCount: number;
        fallbackCount: number;
        averageDurationMs: number;
        p95DurationMs: number;
        totalTokens: number;
        scoreRanges: ScoringScoreRange[];
    }>;
    timeline: Array<{
        bucketStartUtc: string;
        totalCount: number;
        failedCount: number;
        fallbackCount: number;
        averageDurationMs: number;
    }>;
    items: ScoringObservationItem[];
};

export type ScoringObservabilityQuery = {
    fromUtc: string;
    toUtc: string;
    examName?: string;
    examTaskType?: string;
    status?: string;
    scoringMode?: string;
    contentScore?: number;
    contentMaxScore?: number;
    fallbackOnly?: boolean;
    issueOnly?: boolean;
    page?: number;
    pageSize?: number;
};

type AdminApiEnvelope<T> = { success?: boolean; data?: T; errorMessage?: string | null; message?: string };

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '');
const hasVersionedApiPath = (value: string) => /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1'),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const value = payload as Record<string, unknown>;
        const message = value.errorMessage ?? value.message ?? value.error ?? value.title ?? value.detail;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
}

async function requestAdmin<T>(path: string, query: Record<string, string>): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/${trimSlashes(path)}`);
    Object.entries(query).forEach(([key, value]) => value && url.searchParams.set(key, value));
    const headers = new Headers({ Accept: 'application/json' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);
    const response = await fetch(url.toString(), { headers });
    const payload = (response.headers.get('content-type') ?? '').includes('application/json')
        ? await response.json()
        : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    return payload as T;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) return payload as T;
    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) throw new Error(getErrorMessage(envelope, 'Scoring observability request failed'));
    return envelope.data ?? (payload as T);
}

export async function getScoringObservability(query: ScoringObservabilityQuery) {
    return unwrap(
        await requestAdmin<ScoringObservabilityData | AdminApiEnvelope<ScoringObservabilityData>>(
            'AdminScoringObservability',
            {
                fromUtc: query.fromUtc,
                toUtc: query.toUtc,
                examName: query.examName ?? '',
                examTaskType: query.examTaskType ?? '',
                status: query.status ?? '',
                scoringMode: query.scoringMode ?? '',
                contentScore: query.contentScore == null ? '' : String(query.contentScore),
                contentMaxScore: query.contentMaxScore == null ? '' : String(query.contentMaxScore),
                fallbackOnly: query.fallbackOnly ? 'true' : '',
                issueOnly: query.issueOnly ? 'true' : '',
                page: String(query.page ?? 1),
                pageSize: String(query.pageSize ?? 25),
            },
        ),
    );
}

export async function getScoringPracticeDetail(scoringObservationId: string) {
    return unwrap(
        await requestAdmin<ScoringPracticeDetail | AdminApiEnvelope<ScoringPracticeDetail>>(
            `AdminScoringObservability/${encodeURIComponent(scoringObservationId)}/practice-detail`,
            {},
        ),
    );
}

export async function getAiOperationObservability(query: Pick<ScoringObservabilityQuery, 'fromUtc' | 'toUtc' | 'examTaskType'>) {
    return unwrap(
        await requestAdmin<AiOperationObservability | AdminApiEnvelope<AiOperationObservability>>(
            'AdminAiScoring/observability',
            {
                fromUtc: query.fromUtc,
                toUtc: query.toUtc,
                examTaskType: query.examTaskType ?? '',
            },
        ),
    );
}
